import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    Terminal as TerminalIcon,
    Clock,
    Bot,
    Map,
    Activity,
    Menu,
    X
} from 'lucide-react';

const Navigation = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());
    const location = useLocation();

    const formatESTTime = (date) => {
        const estDate = new Date(date.getTime() - (5 * 60 * 60 * 1000));
        const hours = estDate.getUTCHours().toString().padStart(2, '0');
        const minutes = estDate.getUTCMinutes().toString().padStart(2, '0');
        const seconds = estDate.getUTCSeconds().toString().padStart(2, '0');
        return `${hours}:${minutes}:${seconds} EST`;
    };

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    const links = [
        { path: '/', icon: TerminalIcon, label: 'Terminal' },
        { path: '/token-history', icon: Clock, label: 'Token History' },
        { path: '/about', icon: Bot, label: 'About Kyora' },
        { path: '/roadmap', icon: Map, label: 'Roadmap' }
    ];

    const isActive = (path) => {
        if (path === '/') {
            return location.pathname === '/';
        }
        return location.pathname.startsWith(path);
    };

    return (
        <>
            {/* Fixed Navigation Bar */}
            <nav className="h-14 sm:h-16 bg-black/90 border-b border-blue-500/30 backdrop-blur-xl">
                <div className="relative h-full">
                    <div className="absolute inset-0 bg-gradient-to-b from-blue-500/10 to-transparent pointer-events-none" />

                    <div className="max-w-7xl mx-auto h-full">
                        <div className="flex items-center justify-between h-full px-4">
                            {/* Logo Section */}
                            <div className="flex items-center gap-4">
                                <div className="relative group">
                                    <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/30 to-blue-400/0 rounded-full blur-lg group-hover:from-blue-400/40 transition-all duration-700" />
                                    <div className="relative w-10 h-10 rounded-full border-2 border-blue-400/50 overflow-hidden">
                                        <img
                                            src={process.env.PUBLIC_URL + '/kyora.png'}
                                            alt="Kyora AI"
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-blue-500/20 to-transparent" />
                                    </div>
                                </div>
                                {/* Removed 'hidden sm:block' to show on mobile */}
                                <div>
                                    <h1 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-blue-300 to-blue-400">
                                        KYORA AI
                                    </h1>
                                    {/* Keep this part hidden on mobile */}
                                    <div className="hidden sm:flex items-center gap-2">
                                        <Activity className="w-3 h-3 text-emerald-400" />
                                        <span className="text-xs text-blue-200/70">NEURAL NETWORK v1.0</span>
                                    </div>
                                </div>
                            </div>


                            {/* Desktop Links */}
                            <div className="hidden lg:flex items-center gap-6">
                                {links.map((link) => {
                                    const Icon = link.icon;
                                    return (
                                        <Link
                                            key={link.path}
                                            to={link.path}
                                            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300 ${isActive(link.path)
                                                ? 'bg-blue-500/20 text-blue-200'
                                                : 'text-blue-400/70 hover:text-blue-200 hover:bg-blue-500/10'
                                                }`}
                                        >
                                            <Icon className="w-4 h-4" />
                                            <span className="font-medium">{link.label}</span>
                                        </Link>
                                    );
                                })}

                                {/* Social Media Icons */}
                                <div className="flex items-center gap-4 ml-2">
                                    <a
                                        href="https://x.com/ai_kyora"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-400/70 hover:text-blue-200 transition-colors"
                                    >
                                        <svg
                                            className="w-5 h-5"
                                            fill="currentColor"
                                            viewBox="0 0 24 24"
                                            aria-hidden="true"
                                        >
                                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                        </svg>
                                    </a>
                                    <a
                                        href="https://discord.gg/KdT6mkmt"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-400/70 hover:text-blue-200 transition-colors"
                                    >
                                        <svg
                                            className="w-5 h-5"
                                            fill="currentColor"
                                            viewBox="0 0 24 24"
                                            aria-hidden="true"
                                        >
                                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                                        </svg>
                                    </a>
                                    <a
                                        href="https://dexscreener.com/solana/8BDnLQ7YaHjyu1bGvRTq39jUEp7kb73dSTLQgnRypump"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-400/70 hover:text-blue-200 transition-colors"
                                    >
                                        <img
                                            src={process.env.PUBLIC_URL + '/dexscreener.png'}
                                            alt="Dexscreener"
                                            className="w-5 h-5"
                                            style={{
                                                filter: 'invert(67%) sepia(12%) saturate(1640%) hue-rotate(177deg) brightness(101%) contrast(96%) opacity(70%)'
                                            }}
                                        />
                                    </a>
                                </div>
                            </div>

                            {/* System Time */}
                            <div className="hidden sm:flex items-center gap-3 px-4 py-2 bg-blue-950/30 rounded-lg border border-blue-500/30">
                                <Clock className="w-4 h-4 text-blue-400" />
                                <span className="text-sm font-mono text-blue-200">
                                    {formatESTTime(currentTime)}
                                </span>
                            </div>

                            {/* Mobile Menu Button */}
                            <button
                                onClick={() => setIsOpen(!isOpen)}
                                className="lg:hidden p-2 text-blue-400 hover:text-blue-200"
                            >
                                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Mobile Navigation Menu */}
            <div
                className={`
        lg:hidden fixed inset-0 top-14 sm:top-16 bg-black/90 backdrop-blur-xl z-50
        transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}
    `}
            >
                <div className="flex flex-col p-4 gap-2">
                    {links.map((link) => {
                        const Icon = link.icon;
                        return (
                            <Link
                                key={link.path}
                                to={link.path}
                                onClick={() => setIsOpen(false)}
                                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${isActive(link.path)
                                    ? 'bg-blue-500/20 text-blue-200'
                                    : 'text-blue-400/70 hover:text-blue-200 hover:bg-blue-500/10'
                                    }`}
                            >
                                <Icon className="w-5 h-5" />
                                <span className="font-medium">{link.label}</span>
                            </Link>
                        );
                    })}

                    {/* Social Media Links for Mobile */}
                    <div className="border-t border-blue-500/30 mt-2 pt-2">
                        <a
                            href="https://x.com/ai_kyora"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setIsOpen(false)}
                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-blue-400/70 hover:text-blue-200 hover:bg-blue-500/10 transition-all duration-300"
                        >
                            <svg
                                className="w-5 h-5"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                            >
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                            </svg>
                            <span className="font-medium">Follow on X</span>
                        </a>
                        <a
                            href="https://discord.gg/KdT6mkmt"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setIsOpen(false)}
                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-blue-400/70 hover:text-blue-200 hover:bg-blue-500/10 transition-all duration-300"
                        >
                            <svg
                                className="w-5 h-5"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                            >
                                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                            </svg>
                            <span className="font-medium">Join Discord</span>
                        </a>
                        <a
                            href="https://dexscreener.com/solana/8BDnLQ7YaHjyu1bGvRTq39jUEp7kb73dSTLQgnRypump"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setIsOpen(false)}
                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-blue-400/70 hover:text-blue-200 hover:bg-blue-500/10 transition-all duration-300"
                        >
                            <img
                                src={process.env.PUBLIC_URL + '/dexscreener.png'}
                                alt="Dexscreener"
                                className="w-5 h-5"
                                style={{
                                    filter: 'invert(67%) sepia(12%) saturate(1640%) hue-rotate(177deg) brightness(101%) contrast(96%) opacity(70%)'
                                }}
                            />
                            <span className="font-medium">Dexscreener</span>
                        </a>
                    </div>
                </div>
            </div>

        </>
    );
};

export default Navigation;